import { tryUntil } from 'common';
import { RefObject } from 'react';
import { NavigableElement } from '../CfDataGridProps';

export const keyboardNavigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];
export interface ElementRefMatrix {
  refs: RefObject<HTMLElement>[][];
  coords: Coordinates;
}

interface Coordinates {
  rowIndex: number;
  columnIndex: number;
}

interface HTMLElementWithKeydownHandler extends HTMLElement {
  cfKeydownHandler?: (this: HTMLElement, event: KeyboardEvent) => void;
}

type ElementRefMatrixContainer = ElementRefMatrix[][];
type DirectionKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';
type Direction = 'up' | 'down' | 'left' | 'right';

const directionByKey: { [key in DirectionKey]: Direction } = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

export class KeyboardNavigator {
  private _container: ElementRefMatrixContainer;
  private readonly _wraparound: boolean;
  private readonly _onNavigateOutside?: (direction: Direction) => void;
  private readonly _onEnter?: () => boolean;

  constructor(options?: {
    wraparound?: boolean;
    onNavigateOutside?: (direction: Direction) => void;
    onEnter?: () => boolean;
  }) {
    this._container = [[]];
    this._wraparound = options?.wraparound ?? false;
    this._onNavigateOutside = options?.onNavigateOutside;
    this._onEnter = options?.onEnter;
  }

  public get firstEnabledElement(): HTMLElement | null {
    for (let r = 0; r < this._container.length; r++) {
      if (!this._container[r]) continue;
      for (let c = 0; c < this._container[r].length; c++) {
        if (!this._container[r][c]) continue;
        return this.getClosestEnabledElementNext(this._container[r][c], { rowIndex: -1, columnIndex: -1 });
      }
    }

    return null;
  }

  public get lastEnabledElement(): HTMLElement | null {
    for (let r = this._container.length; r >= 0; r--) {
      if (!this._container[r]) continue;
      for (let c = this._container[r].length; c >= 0; c--) {
        if (!this._container[r][c]) continue;
        return this.getClosestEnabledElementPrevious(this._container[r][c], {
          rowIndex: Number.MAX_VALUE,
          columnIndex: Number.MAX_VALUE,
        });
      }
    }

    return null;
  }

  public closestEnabledElement = (
    rowIndex: number,
    elementIndex?: number
  ): { rowIndex: number; columnIndex: number; elem: HTMLElement | null } | null => {
    for (let r = rowIndex; r < this._container.length; r++) {
      if (!this._container[r]) continue;
      for (let c = elementIndex ?? 0; c < this._container[r].length; c++) {
        if (!this._container[r][c]) continue;
        return {
          rowIndex: r,
          columnIndex: c,
          elem: this.getClosestEnabledElementNext(this._container[r][c], {
            rowIndex: (elementIndex ?? 0) - 1,
            columnIndex: (elementIndex ?? 0) - 1,
          }),
        };
      }
    }

    return null;
  };

  public reset = (): void => {
    for (let r = 0; r < this._container.length; r++) {
      if (!this._container[r]) continue;
      for (let c = 0; c < this._container[r].length; c++) {
        if (!this._container[r][c]) continue;
        this.removeEventListeners(this._container[r][c]);
      }
    }

    this._container = [[]];
  };

  public registerNavigableElement = (navigableElement: NavigableElement, coords: Coordinates): void => {
    const { rowIndex, columnIndex } = coords;

    const row = this._container[rowIndex] ?? [];
    const refMatrix = row[columnIndex] ?? {
      refs: [],
      coords,
    };

    refMatrix.refs[navigableElement.localRowIndex] = refMatrix.refs[navigableElement.localRowIndex] ?? [];
    refMatrix.refs[navigableElement.localRowIndex][navigableElement.localColumnIndex] = navigableElement.ref;

    row[columnIndex] = refMatrix;
    this._container[rowIndex] = row;

    this.addEventListenerIfNecessary(navigableElement, refMatrix);
  };

  public focusFirstEnabledElement = (): void => {
    tryUntil(
      () => {
        const ele = this.firstEnabledElement;
        if (!ele) return false;

        setTimeout(() => ele.focus(), 0);
        return true;
      },
      5,
      0
    );
  };

  public focusClosestEnabledElement = (rowIndex: number, elementIndex?: number): void => {
    tryUntil(
      () => {
        const ele = this.getEnabledElement(rowIndex, elementIndex);
        //const ele = this.closestEnabledElement(rowIndex, elementIndex);

        if (!ele) return false;

        setTimeout(() => ele.focus(), 0);
        return true;
      },
      5,
      0
    );
  };

  public getEnabledElement = (rowIndex: number, columnIndex?: number): HTMLElement | null => {
    for (let r = rowIndex; r < this._container.length; r++) {
      if (!this._container[r]) continue;
      for (let c = columnIndex ?? 0; c < this._container[r].length; c++) {
        if (!this._container[r][c]) continue;
        return this.getClosestEnabledElementNext(this._container[r][c], {
          rowIndex: -1,
          columnIndex: -1,
        });
      }
    }

    return null;
  };

  public focusElement = (rowIndex: number, elementIndex: number | undefined): void => {
    tryUntil(
      () => {
        const res = this.closestEnabledElement(rowIndex, elementIndex);
        //console.log('focus', rowIndex, elementIndex, res);

        if (res?.rowIndex != rowIndex) {
          res?.elem?.scrollIntoView({ behavior: 'auto' });
          return false;
        }

        setTimeout(() => res.elem?.focus());
        return true;
      },
      36,
      0
    );
  };

  private removeEventListener = (element: HTMLElementWithKeydownHandler) => {
    const existingHandler = element.cfKeydownHandler;
    if (existingHandler) {
      element.removeEventListener('keydown', existingHandler);
      delete element.cfKeydownHandler;
    }
  };

  private addEventListenerIfNecessary = (navigableElement: NavigableElement, refMatrix: ElementRefMatrix) => {
    const element: HTMLElementWithKeydownHandler | null = navigableElement.ref.current;
    if (!element || element.cfKeydownHandler) return;

    this.removeEventListener(element);
    element.addEventListener(
      'keydown',
      (element.cfKeydownHandler = (event: KeyboardEvent) => {
        if (keyboardNavigationKeys.includes(event.key)) {
          event.preventDefault();
          let neighbor: HTMLElement | null;
          let direction: Direction;
          const coords = {
            rowIndex: navigableElement.localRowIndex,
            columnIndex: navigableElement.localColumnIndex,
          };
          if (event.key === 'Enter') {
            if (this._onEnter != null && this._onEnter()) {
              // Handled, override defalut behaviour
              return;
            }
            if (event.shiftKey) {
              direction = 'up';
              neighbor = this.getClosestEnabledElementPrevious(refMatrix, coords);
            } else {
              direction = 'down';
              neighbor = this.getClosestEnabledElementNext(refMatrix, coords);
            }
          } else {
            direction = directionByKey[event.key as DirectionKey];
            neighbor = this.getNeighborElement(refMatrix, coords, direction);
          }
          if (neighbor) {
            neighbor.focus();
          } else {
            this._onNavigateOutside?.(direction);
          }
        }
      })
    );
  };

  private removeEventListeners = (refMatrix: ElementRefMatrix) => {
    for (let r = 0; r < refMatrix.refs.length; r++) {
      if (!refMatrix.refs[r]) continue;
      for (let c = 0; c < refMatrix.refs[r].length; c++) {
        if (!refMatrix.refs[r][c]) continue;

        const element: HTMLElementWithKeydownHandler | null = refMatrix.refs[r][c].current;
        if (element) {
          this.removeEventListener(element);
        }
      }
    }
  };

  private getClosestEnabledElementNext = (refMatrix: ElementRefMatrix, coords: Coordinates): HTMLElement | null => {
    const refs = this.transpose(refMatrix.refs);
    let element: HTMLElement | null = null;
    for (let r = 0; r < refs.length && !element; r++) {
      if (!refs[r]) continue;
      for (let c = 0; c < refs[r].length && !element; c++) {
        if (refs[r][c]) {
          const { ref, originalCoords } = refs[r][c];
          if (
            (originalCoords.rowIndex > coords.rowIndex || originalCoords.columnIndex > coords.columnIndex) &&
            this.isElementRefEnabled(ref)
          ) {
            element = ref.current;
          }
        }
      }
    }

    if (!element) {
      const neighborRefMatrix = this.getNeighborElementRefMatrixNext(refMatrix);
      if (neighborRefMatrix) {
        element = this.getClosestEnabledElementNext(neighborRefMatrix, { rowIndex: -1, columnIndex: -1 });
      }
    }

    return element;
  };

  private getNeighborElementRefMatrixNext = (refMatrix: ElementRefMatrix): ElementRefMatrix | null => {
    let neighbor: ElementRefMatrix | null = null;
    for (let r = refMatrix.coords.rowIndex; r < this._container.length && !neighbor; r++) {
      if (!this._container[r]) continue;
      let c = r === refMatrix.coords.rowIndex ? refMatrix.coords.columnIndex + 1 : 0;
      for (; c < this._container[r].length && !neighbor; c++) {
        neighbor = this._container[r][c];
      }
    }

    return neighbor;
  };

  private getClosestEnabledElementPrevious = (refMatrix: ElementRefMatrix, coords: Coordinates): HTMLElement | null => {
    const refs = this.transpose(refMatrix.refs);
    let element: HTMLElement | null = null;
    for (let r = refs.length - 1; r >= 0 && !element; r--) {
      if (!refs[r]) continue;
      for (let c = refs[r].length - 1; c >= 0 && !element; c--) {
        if (refs[r][c]) {
          const { ref, originalCoords } = refs[r][c];

          if (
            (originalCoords.rowIndex < coords.rowIndex || originalCoords.columnIndex < coords.columnIndex) &&
            this.isElementRefEnabled(ref)
          ) {
            element = ref.current;
          }
        }
      }
    }

    if (!element) {
      const neighborRefMatrix = this.getNeighborElementRefMatrixPrevious(refMatrix);
      if (neighborRefMatrix) {
        element = this.getClosestEnabledElementPrevious(neighborRefMatrix, {
          rowIndex: neighborRefMatrix.refs.length,
          columnIndex: -1,
        });
      }
    }

    return element;
  };

  private getNeighborElementRefMatrixPrevious = (refMatrix: ElementRefMatrix): ElementRefMatrix | null => {
    let neighbor: ElementRefMatrix | null = null;
    for (let r = refMatrix.coords.rowIndex; r >= 0 && !neighbor; r--) {
      if (!this._container[r]) continue;
      let c = r === refMatrix.coords.rowIndex ? refMatrix.coords.columnIndex - 1 : this._container[r].length - 1;
      for (; c >= 0 && !neighbor; c--) {
        neighbor = this._container[r][c];
      }
    }

    return neighbor;
  };

  private transpose = (
    refs: RefObject<HTMLElement>[][]
  ): { ref: RefObject<HTMLElement>; originalCoords: Coordinates }[][] => {
    const transposedRefs: { ref: RefObject<HTMLElement>; originalCoords: Coordinates }[][] = [];

    for (let r = 0; r < refs.length; r++) {
      if (!refs[r]) continue;
      for (let c = 0; c < refs[r].length; c++) {
        if (!refs[r][c]) continue;
        transposedRefs[c] = transposedRefs[c] ?? [];
        transposedRefs[c][r] = { ref: refs[r][c], originalCoords: { rowIndex: r, columnIndex: c } };
      }
    }

    return transposedRefs;
  };

  private getNeighborElement = (
    refMatrix: ElementRefMatrix,
    coords: Coordinates,
    direction: Direction
  ): HTMLElement | null => {
    switch (direction) {
      case 'up':
        return this.getNeighborElementUp(refMatrix, coords);
      case 'down':
        return this.getNeighborElementDown(refMatrix, coords);
      case 'left':
        return this.getNeighborElementLeft(refMatrix, coords);
      case 'right':
        return this.getNeighborElementRight(refMatrix, coords);
    }
  };

  private getNeighborElementUp = (refMatrix: ElementRefMatrix, coords: Coordinates) => {
    let element: HTMLElement | null = this.getClosestEnabledElementUp(refMatrix, coords);

    if (!element) {
      for (let r = refMatrix.coords.rowIndex - 1; r >= 0 && !element; r--) {
        if (!this._container[r]?.length) continue;

        const topRefMatrix = this._container[r][refMatrix.coords.columnIndex];
        if (topRefMatrix) {
          element = this.getClosestEnabledElementUp(topRefMatrix, {
            rowIndex: topRefMatrix.refs.length,
            columnIndex: coords.columnIndex,
          });
        }
        for (
          let i = 1;
          (0 <= refMatrix.coords.columnIndex - i || refMatrix.coords.columnIndex + i < this._container[r].length) &&
          !element;
          i++
        ) {
          const leftColumn = refMatrix.coords.columnIndex - i;
          const topLeftRefMatrix = this._container[r][leftColumn];
          if (topLeftRefMatrix) {
            element = this.getClosestEnabledElementDiagonal(topLeftRefMatrix, 'bottom-right');
            if (element) break;
          }
          const rightColumn = refMatrix.coords.columnIndex + i;
          const topRightRefMatrix = this._container[r][rightColumn];
          if (topRightRefMatrix) {
            element = this.getClosestEnabledElementDiagonal(topRightRefMatrix, 'bottom-left');
          }
        }
      }
    }

    return element;
  };

  private getClosestEnabledElementUp = (refMatrix: ElementRefMatrix, coords: Coordinates): HTMLElement | null => {
    const { rowIndex, columnIndex } = coords;

    let element: HTMLElement | null = null;
    for (let r = rowIndex - 1; r >= 0 && !element; r--) {
      if (!refMatrix.refs[r]) continue;
      element = this.getClosestEnabledElementInRow(refMatrix.refs[r], columnIndex);
    }

    return element;
  };

  private getNeighborElementDown = (refMatrix: ElementRefMatrix, coords: Coordinates) => {
    let element: HTMLElement | null = this.getClosestEnabledElementDown(refMatrix, coords);

    if (!element) {
      for (let r = refMatrix.coords.rowIndex + 1; r < this._container.length && !element; r++) {
        if (!this._container[r]?.length) continue;

        const bottomRefMatrix = this._container[r][refMatrix.coords.columnIndex];
        if (bottomRefMatrix) {
          element = this.getClosestEnabledElementDown(bottomRefMatrix, {
            rowIndex: -1,
            columnIndex: coords.columnIndex,
          });
        }
        for (
          let i = 1;
          (0 <= refMatrix.coords.columnIndex - i || refMatrix.coords.columnIndex + i < this._container[r].length) &&
          !element;
          i++
        ) {
          const leftColumn = refMatrix.coords.columnIndex - i;
          const bottomLeftRefMatrix = this._container[r][leftColumn];
          if (bottomLeftRefMatrix) {
            element = this.getClosestEnabledElementDiagonal(bottomLeftRefMatrix, 'top-right');
            if (element) break;
          }
          const rightColumn = refMatrix.coords.columnIndex + i;
          const bottomRightRefMatrix = this._container[r][rightColumn];
          if (bottomRightRefMatrix) {
            element = this.getClosestEnabledElementDiagonal(bottomRightRefMatrix, 'top-left');
          }
        }
      }
    }

    return element;
  };

  private getClosestEnabledElementDown = (refMatrix: ElementRefMatrix, coords: Coordinates): HTMLElement | null => {
    const { rowIndex, columnIndex } = coords;

    let element: HTMLElement | null = null;
    for (let r = rowIndex + 1; r < refMatrix.refs.length && !element; r++) {
      if (!refMatrix.refs[r]) continue;
      element = this.getClosestEnabledElementInRow(refMatrix.refs[r], columnIndex);
    }

    return element;
  };

  private getNeighborElementLeft = (refMatrix: ElementRefMatrix, coords: Coordinates): HTMLElement | null => {
    let element: HTMLElement | null = this.getClosestEnabledElementLeft(refMatrix, coords);

    if (!element) {
      for (let c = refMatrix.coords.columnIndex - 1; c >= 0 && !element; c--) {
        const leftRefMatrix = this._container[refMatrix.coords.rowIndex][c];
        if (leftRefMatrix) {
          element = this.getClosestEnabledElementLeft(leftRefMatrix, {
            rowIndex: coords.rowIndex,
            columnIndex: leftRefMatrix.refs.length,
          });
        }
      }
    }

    for (let r = refMatrix.coords.rowIndex - 1; r >= 0 && this._wraparound && !element; r--) {
      const aboveRow = this._container[r];
      if (aboveRow) {
        for (let c = aboveRow.length - 1; c >= 0 && !element; c--) {
          const wraparoundRefMatrix = aboveRow[c];
          if (wraparoundRefMatrix) {
            element = this.getClosestEnabledElementLeft(wraparoundRefMatrix, {
              rowIndex: coords.rowIndex,
              columnIndex: wraparoundRefMatrix.refs.length,
            });
          }
        }
      }
    }

    return element;
  };

  private getClosestEnabledElementLeft = (refMatrix: ElementRefMatrix, coords: Coordinates): HTMLElement | null => {
    let element: HTMLElement | null = null;

    const row = refMatrix.refs[coords.rowIndex];
    if (row) {
      element = this.getClosestEnabledElementInRowByDirection(row, coords.columnIndex, 'left');
    }
    for (let i = 1; (0 <= coords.rowIndex - i || coords.rowIndex + i < refMatrix.refs.length) && !element; i++) {
      const topRow = refMatrix.refs[coords.rowIndex - i];
      if (topRow) {
        element = this.getClosestEnabledElementInRowByDirection(topRow, coords.columnIndex, 'left');
        if (element) break;
      }
      const bottomRow = refMatrix.refs[coords.rowIndex + i];
      if (bottomRow) {
        element = this.getClosestEnabledElementInRowByDirection(bottomRow, coords.columnIndex, 'left');
      }
    }

    return element;
  };

  private getNeighborElementRight = (refMatrix: ElementRefMatrix, coords: Coordinates): HTMLElement | null => {
    let element: HTMLElement | null = this.getClosestEnabledElementRight(refMatrix, coords);

    if (!element) {
      for (
        let c = refMatrix.coords.columnIndex + 1;
        c < this._container[refMatrix.coords.rowIndex].length && !element;
        c++
      ) {
        const rightRefMatrix = this._container[refMatrix.coords.rowIndex][c];
        if (rightRefMatrix) {
          element = this.getClosestEnabledElementRight(rightRefMatrix, {
            rowIndex: coords.rowIndex,
            columnIndex: -1,
          });
        }
      }

      for (let r = refMatrix.coords.rowIndex + 1; r < this._container.length && this._wraparound && !element; r++) {
        const belowRow = this._container[r];
        if (belowRow) {
          for (let c = 0; c < belowRow.length && !element; c++) {
            const wraparoundRefMatrix = belowRow[c];
            if (wraparoundRefMatrix) {
              element = this.getClosestEnabledElementRight(wraparoundRefMatrix, {
                rowIndex: coords.rowIndex,
                columnIndex: -1,
              });
            }
          }
        }
      }
    }

    return element;
  };

  private getClosestEnabledElementRight = (refMatrix: ElementRefMatrix, coords: Coordinates): HTMLElement | null => {
    let element: HTMLElement | null = null;

    const row = refMatrix.refs[coords.rowIndex];
    if (row) {
      element = this.getClosestEnabledElementInRowByDirection(row, coords.columnIndex, 'right');
    }
    for (let i = 1; (0 <= coords.rowIndex - i || coords.rowIndex + i < refMatrix.refs.length) && !element; i++) {
      const topRow = refMatrix.refs[coords.rowIndex - i];
      if (topRow) {
        element = this.getClosestEnabledElementInRowByDirection(topRow, coords.columnIndex, 'right');
        if (element) break;
      }
      const bottomRow = refMatrix.refs[coords.rowIndex + i];
      if (bottomRow) {
        element = this.getClosestEnabledElementInRowByDirection(bottomRow, coords.columnIndex, 'right');
      }
    }

    return element;
  };

  private getClosestEnabledElementInRowByDirection = (
    refs: RefObject<HTMLElement>[],
    startingColumnIndex: number,
    direction: 'left' | 'right'
  ) => {
    let element: HTMLElement | null = null;
    if (direction === 'left') {
      for (let c = startingColumnIndex - 1; c >= 0 && !element; c--) {
        const ref = refs[c];
        if (this.isElementRefEnabled(ref)) {
          element = ref.current;
        }
      }
    } else {
      for (let c = startingColumnIndex + 1; c < refs.length && !element; c++) {
        const ref = refs[c];
        if (this.isElementRefEnabled(ref)) {
          element = ref.current;
        }
      }
    }

    return element;
  };

  private getClosestEnabledElementDiagonal = (
    refMatrix: ElementRefMatrix,
    diagonal: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  ): HTMLElement | null => {
    let element: HTMLElement | null = null;
    switch (diagonal) {
      case 'top-left': {
        for (let r = 0; r < refMatrix.refs.length && !element; r++) {
          if (!refMatrix.refs[r]) continue;
          for (let c = 0; c < refMatrix.refs[r].length && !element; c++) {
            if (!refMatrix.refs[r][c]) continue;
            const ref = refMatrix.refs[r][c];
            if (this.isElementRefEnabled(ref)) {
              element = ref.current;
            }
          }
        }
        break;
      }
      case 'top-right': {
        for (let r = 0; r < refMatrix.refs.length && !element; r++) {
          if (!refMatrix.refs[r]) continue;
          for (let c = refMatrix.refs[r].length - 1; c >= 0 && !element; c--) {
            if (!refMatrix.refs[r][c]) continue;
            const ref = refMatrix.refs[r][c];
            if (this.isElementRefEnabled(ref)) {
              element = ref.current;
            }
          }
        }
        break;
      }
      case 'bottom-left': {
        for (let r = refMatrix.refs.length - 1; r >= 0 && !element; r--) {
          if (!refMatrix.refs[r]) continue;
          for (let c = 0; c < refMatrix.refs[r].length && !element; c++) {
            if (!refMatrix.refs[r][c]) continue;
            const ref = refMatrix.refs[r][c];
            if (this.isElementRefEnabled(ref)) {
              element = ref.current;
            }
          }
        }
        break;
      }
      case 'bottom-right': {
        for (let r = refMatrix.refs.length - 1; r >= 0 && !element; r--) {
          if (!refMatrix.refs[r]) continue;
          for (let c = refMatrix.refs[r].length - 1; c >= 0 && !element; c--) {
            if (!refMatrix.refs[r][c]) continue;
            const ref = refMatrix.refs[r][c];
            if (this.isElementRefEnabled(ref)) {
              element = ref.current;
            }
          }
        }
        break;
      }
    }

    return element;
  };

  private getClosestEnabledElementInRow = (refs: RefObject<HTMLElement>[], columnIndex: number): HTMLElement | null => {
    const indices: number[] = Object.keys(refs.filter((r) => this.isElementRefEnabled(r)))
      .map((k) => Number(k))
      .filter((k) => !isNaN(k));
    indices.sort((a, b) => Math.abs(a - columnIndex) - Math.abs(b - columnIndex));
    if (indices.length > 0 && refs[indices[0]].current) {
      return refs[indices[0]].current;
    } else {
      return null;
    }
  };

  private isElementRefEnabled = (ref?: RefObject<HTMLElement>): boolean => {
    const element = ref?.current;
    return !!(element && !element.hasAttribute('disabled'));
  };
}
